import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { NumInput, SelectInput } from "@/components/FieldInput";
import { PRODUCT_CATEGORIES, productsApi } from "@/lib/api";
import { money } from "@/lib/format";

const blank = { name: "", category: PRODUCT_CATEGORIES[0], premium: "" };

const ProductForm = ({ value, onChange, onSubmit, onCancel, prefix }) => (
  <div className="prod-grid">
    <Input data-testid={`${prefix}-name`} placeholder="Product name (e.g. Term 20 - $500K)" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className="bg-white h-9" />
    <SelectInput id={`${prefix}-category`} value={value.category} options={PRODUCT_CATEGORIES} onChange={(v) => onChange({ ...value, category: v })} />
    <NumInput id={`${prefix}-premium`} placeholder="Monthly premium" value={value.premium} onChange={(v) => onChange({ ...value, premium: v })} />
    <div className="flex gap-1">
      <Button size="sm" onClick={onSubmit} data-testid={`${prefix}-save`} className="gap-1">{onCancel ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{onCancel ? "Save" : "Add"}</Button>
      {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${prefix}-cancel`}><X className="h-4 w-4" /></Button>}
    </div>
  </div>
);

export default function ProductsPage() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [draft, setDraft] = useState(blank);
  const [editing, setEditing] = useState(null);
  const load = () => productsApi.list().then(setProducts).catch(() => toast.error("Could not load products"));
  useEffect(() => { load(); }, []);

  const payload = (p) => ({ name: p.name.trim(), category: p.category, premium: Number(p.premium) || 0 });
  const add = async () => {
    if (!draft.name.trim()) return toast.error("Enter a product name");
    await productsApi.create(payload(draft));
    toast.success("Product added");
    setDraft({ ...blank, category: draft.category });
    load();
  };
  const save = async () => {
    if (!editing.name.trim()) return toast.error("Enter a product name");
    await productsApi.update(editing.id, payload(editing));
    toast.success("Product updated");
    setEditing(null);
    load();
  };
  const remove = async (id) => { await productsApi.remove(id); toast.success("Product removed"); load(); };

  return (
    <div className="min-h-screen">
      <AppHeader>
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="products-back-button" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
      </AppHeader>
      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-5 sm:py-8 space-y-4">
        <div>
          <p className="metric-label">Advisor settings</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Product library</h1>
          <p className="text-sm text-slate-500 mt-1">Save the products you usually recommend. In any client's Insurance or Retirement tab, use the library button next to a recommendation to fill in the product and premium. You can still adjust both for each client.</p>
        </div>
        <section className="panel" data-testid="product-add-form">
          <div className="panel-head"><h3>Add product</h3></div>
          <ProductForm value={draft} onChange={setDraft} onSubmit={add} prefix="product-new" />
        </section>
        {PRODUCT_CATEGORIES.map((cat) => {
          const list = products.filter((p) => p.category === cat);
          return (
            <section key={cat} className="panel" data-testid={`product-group-${cat.split(" ")[0].toLowerCase()}`}>
              <div className="panel-head"><h3>{cat}</h3><span className="text-xs text-slate-400">{list.length} saved</span></div>
              {!list.length && <p className="text-sm text-slate-400">No products yet.</p>}
              {list.map((p) => editing?.id === p.id ? (
                <div key={p.id} className="py-1"><ProductForm value={editing} onChange={setEditing} onSubmit={save} onCancel={() => setEditing(null)} prefix={`product-edit-${p.id}`} /></div>
              ) : (
                <div key={p.id} className="flex items-center justify-between gap-3 py-2 border-b border-dashed border-slate-100 last:border-0" data-testid={`product-row-${p.id}`}>
                  <span className="font-medium text-slate-900">{p.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-slate-700">{money(p.premium, "CAD")}/mo</span>
                    <Button variant="ghost" size="icon" onClick={() => setEditing({ ...p })} data-testid={`product-edit-${p.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`product-delete-${p.id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                </div>
              ))}
            </section>
          );
        })}
      </main>
    </div>
  );
}
